// src/api/biometrico.js
// ⭐ HUELLA: usa el AGENTE LOCAL vía Tailscale Funnel
import { apiFetch } from './index.js';

// ⭐ URL FIJA de Tailscale Funnel (NUNCA cambia)
import { getAgenteUrl } from './acceso.js';
const AGENTE_URL_DEFAULT = 'https://desktop-qagollm.tailb8d19f.ts.net';
const getAgentBaseUrl = () => getAgenteUrl() || AGENTE_URL_DEFAULT;

// =====================================================
// 🔥 ESTADO DEL LECTOR (AGENTE LOCAL)
// =====================================================

/**
 * Verifica si el lector FS88H está conectado.
 * Llama al AGENTE LOCAL, no al backend Docker.
 */
export const obtenerEstadoLector = async () => {
  try {
    console.log('🔍 Consultando estado del lector FS88H al agente local...');
    const response = await fetch(`${getAgentBaseUrl()}/estado-huella`, {
      method: 'GET',
      signal: AbortSignal.timeout(15000)
    });
    const data = await response.json();
    console.log('✅ Estado del lector:', data);
    return {
      success: true,
      conectado: data.conectado || false,
      mensaje: data.mensaje,
      data
    };
  } catch (error) {
    console.error('❌ Error consultando estado del lector:', error);
    return {
      success: false,
      conectado: false,
      mensaje: '❌ No se pudo contactar al agente local'
    };
  }
};

/**
 * Conectar lector FS88H (vía agente local).
 */
export const conectarLector = async () => {
  try {
    console.log('🔌 Conectando lector FS88H...');
    const response = await fetch(`${getAgentBaseUrl()}/conectar-lector`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(20000)
    });
    const data = await response.json();
    return {
      success: data.success || false,
      conectado: data.conectado || false,
      mensaje: data.mensaje,
      data
    };
  } catch (error) {
    console.error('❌ Error conectando lector:', error);
    return { success: false, conectado: false, mensaje: error.message };
  }
};

/**
 * Desconectar lector FS88H (vía agente local).
 */
export const desconectarLector = async () => {
  try {
    console.log('🔌 Desconectando lector FS88H...');
    const response = await fetch(`${getAgentBaseUrl()}/desconectar-lector`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    return { success: data.success || false, mensaje: data.mensaje };
  } catch (error) {
    console.error('❌ Error desconectando lector:', error);
    return { success: false, mensaje: error.message };
  }
};

// =====================================================
// 🔥 ESCUCHA CONTINUA (AGENTE LOCAL)
// =====================================================

/**
 * Activa la escucha continua del lector.
 * El agente queda escuchando en bucle y valida automáticamente.
 */
export const activarEscuchaGym = async () => {
  try {
    console.log('🎧 Activando escucha continua en el agente...');
    const response = await fetch(`${getAgentBaseUrl()}/activar-escucha`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    return { success: data.success || false, activo: data.activo || false, mensaje: data.mensaje };
  } catch (error) {
    console.error('❌ Error activando escucha:', error);
    return { success: false, activo: false, mensaje: error.message };
  }
};

/**
 * Desactiva la escucha continua.
 */
export const desactivarEscuchaGym = async () => {
  try {
    console.log('🔇 Desactivando escucha continua...');
    const response = await fetch(`${getAgentBaseUrl()}/desactivar-escucha`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    return { success: data.success || false, activo: data.activo || false };
  } catch (error) {
    return { success: false, activo: false, mensaje: error.message };
  }
};

/**
 * Estado de la escucha continua.
 */
export const estadoEscuchaGym = async () => {
  try {
    const response = await fetch(`${getAgentBaseUrl()}/estado-escucha`);
    const data = await response.json();
    return { success: true, activo: data.activo || false, lectorConectado: data.lectorConectado || false };
  } catch (error) {
    return { success: false, activo: false, lectorConectado: false };
  }
};

// =====================================================
// 🔥 IDENTIFICACIÓN POR HUELLA (vía agente local)
// =====================================================

/**
 * Identifica un socio por huella (1:N).
 * El AGENTE captura, valida contra el backend y abre el torniquete.
 */
export const identificarSocioPorHuella = async () => {
  try {
    console.log('🔍 Solicitando identificación al agente local...');
    const response = await fetch(`${getAgentBaseUrl()}/identificar-huella`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(30000)
    });
    const data = await response.json();
    console.log('✅ Resultado identificación:', data);
    return { success: true, ...data };
  } catch (error) {
    console.error('❌ Error identificando socio:', error);
    return {
      success: false,
      permitido: false,
      mensaje: 'Error al comunicarse con el agente local: ' + error.message
    };
  }
};

/**
 * Estado general del agente local.
 */
export const estadoBiometrico = async () => {
  try {
    const response = await fetch(`${getAgentBaseUrl()}/`);
    const data = await response.json();
    return { success: true, data: { conectado: !!data.nombre }, ...data };
  } catch (error) {
    return { success: false, error: error.message, data: { conectado: false } };
  }
};

// =====================================================
// 🔥 REGISTRO DE HUELLA (vía agente local)
// =====================================================

/**
 * Captura una huella del lector FS88H (para registro de nuevo socio).
 * Usa el AGENTE LOCAL porque el backend Docker no tiene acceso al USB.
 */
export const capturarHuella = async () => {
  try {
    console.log('📸 Capturando huella...');
    const response = await fetch(`${getAgentBaseUrl()}/capturar-huella`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(30000)
    });
    const data = await response.json();
    return {
      success: data.success ?? false,
      imagen: data.imagen,
      size: data.size,
      width: data.width,
      height: data.height,
      error: data.error
    };
  } catch (error) {
    console.error('❌ Error capturando huella:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 🔥 Activa el modo registro de huella para un cliente (socio del gym).
 * El AGENTE LOCAL captura la huella y la envía al backend para guardarla.
 * Usado desde MembresiasList.vue.
 */
export const activarModoRegistroClienteAgente = async (idCliente) => {
  try {
    console.log(`📝 Activando modo registro para cliente ${idCliente} en el agente local...`);
    const response = await fetch(`${getAgentBaseUrl()}/modo-registro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idCliente }),
      signal: AbortSignal.timeout(35000)
    });
    const data = await response.json();
    console.log('✅ Respuesta modo registro:', data);
    const esExito = response.ok && (data.success === true || data.exito === true || data.data?.exito === true);
    return {
      success: esExito,
      data: data.data || data,
      mensaje: data.mensaje || data.data?.mensaje || data.error,
      error: data.error
    };
  } catch (error) {
    console.error('❌ Error activando modo registro:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Guarda la huella de un usuario en la BD.
 */
export const guardarHuella = async (idUsuario, template, formato = 'SourceAFIS') => {
  try {
    const response = await apiFetch('/huella/registrar-huella', {
      method: 'POST',
      body: JSON.stringify({ idUsuario, templateData: template })
    });
    return {
      success: response?.exito ?? response?.success ?? false,
      mensaje: response?.mensaje,
      ...response
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Verifica si un usuario ya tiene huella registrada.
 */
export const tieneHuella = async (idUsuario) => {
  try {
    const response = await apiFetch(`/huella/usuario-tiene-huella/${idUsuario}`);
    return { success: true, data: response, ...response };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// =====================================================
// EXPORT
// =====================================================
export default {
  // Estado
  obtenerEstadoLector,
  conectarLector,
  desconectarLector,

  // Escucha
  activarEscuchaGym,
  desactivarEscuchaGym,
  estadoEscuchaGym,

  // Identificación
  identificarSocioPorHuella,
  estadoBiometrico,

  // Registro
  capturarHuella,
  activarModoRegistroClienteAgente,
  guardarHuella,
  tieneHuella
};